import { useEffect, useState } from 'react';
import {
  TokenValidationReason,
  ValidationStatus,
} from '@/features/onboarding/types/enums';
import type { TokenValidationResult } from '@/features/onboarding/types/onboarding.types';

export { ValidationStatus };

interface UseTokenValidationOptions {
  /** Token from URL params */
  token: string | undefined;
  /** Function to validate the token */
  validateToken: (token: string) => Promise<TokenValidationResult>;
}

interface UseTokenValidationResult {
  status: ValidationStatus;
  validationResult: TokenValidationResult | null;
  error: string | null;
}

/**
 * Hook for validating onboarding magic link tokens
 *
 * Handles:
 * - Missing token validation
 * - API call to validate token
 * - Error handling
 */
export function useTokenValidation({
  token,
  validateToken,
}: UseTokenValidationOptions): UseTokenValidationResult {
  const [status, setStatus] = useState<ValidationStatus>(
    ValidationStatus.VALIDATING
  );
  const [validationResult, setValidationResult] =
    useState<TokenValidationResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const validate = async () => {
      if (!token) {
        setStatus(ValidationStatus.INVALID);
        setError('No token provided');

        return;
      }

      let data: TokenValidationResult;

      try {
        data = await validateToken(token);
      } catch (err) {
        console.error('Error validating token:', err);
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Network error - please try again';
        setError(errorMessage);
        setStatus(ValidationStatus.INVALID);

        return;
      }

      if (data.valid && data.user_id) {
        setValidationResult(data);
        setStatus(ValidationStatus.VALID);
      } else if (data.reason === TokenValidationReason.COMPLETED) {
        setValidationResult(data);
        setStatus(ValidationStatus.ALREADY_COMPLETED);
      } else if (data.reason === TokenValidationReason.EXPIRED) {
        setValidationResult(data);
        setStatus(ValidationStatus.EXPIRED);
      } else {
        setValidationResult(data);
        setStatus(ValidationStatus.INVALID);
      }
    };

    validate();
  }, [token, validateToken]);

  return {
    status,
    validationResult,
    error,
  };
}
